/* eslint-disable @typescript-eslint/no-explicit-any */

import { sanitiseAnything, SanitisedElement } from '@lib/parse/sanitiseElements';
import { maybeGet, slugify, Typename } from '@liquorice/allsorts-craftcms-nextjs';
import { BlocksFragment, SocialLinksFragment } from 'gql/_generated/blocks.generated';

export type RawBlocks = BlocksFragment | SocialLinksFragment;

/**
 * __typename of top level Block
 */
type BlockTypename = Typename<RawBlocks>;

// ----------------------------------------------------------------------------------------------------
// --- Extracted sanitised types ---

export type SanitisedBlock<T extends BlockTypename = BlockTypename> = SanitisedElement<T>;
export type Block<T extends BlockTypename = BlockTypename> = SanitisedElement<T> & {
  _blockMeta?: BlockMeta;
};

export type BlockMeta = {
  inView?: boolean;
  index: number;
  first: boolean;
  firstOfType: boolean;
  nthOfType: number;
  last: boolean;
  previousBlock?: Block;
};

// ----------------------------------------------------------------------------------------------------

/**
 * Create a {@link Block} consumer React Component by providing
 * the `__typename` of `BlockType` as T
 */
export const createBlock = <T extends BlockTypename, P = NoProps, R = JSX.Element | null>(
  fn: (props: Partial<Block<T>> & P) => R
) => fn;

export const sanitiseBlocks = (maybeBlocks: MaybeArrayOf<BlocksFragment>) => {
  return sanitiseAnything.many(maybeBlocks) as SanitisedBlock[];
};

export const parseSanitisedBlocks = (sanitisedBlocks: SanitisedBlock[]): Block[] => {
  const nthOfTypeCount: Record<string, number> = {};

  return sanitisedBlocks.map((v, i): Block => {
    const type = v.__typename;

    nthOfTypeCount[type] = type in nthOfTypeCount ? nthOfTypeCount[type] + 1 : 0;
    return {
      ...v,
      _blockMeta: {
        nthOfType: nthOfTypeCount[type],
        index: i,
        first: i === 0,
        firstOfType: !!sanitisedBlocks.find((x, j) => j < i && x.__typename === v.__typename),
        last: i === sanitisedBlocks.length - 1,
        previousBlock: sanitisedBlocks[i - 1],
      },
    };
  });
};

export const parseBlocks = (maybeBlocks: MaybeArrayOf<BlocksFragment>): Block[] => {
  return parseSanitisedBlocks(sanitiseBlocks(maybeBlocks));
};

export type BlockView = {
  anchorLabel?: string;
  anchor?: string;
  block: Block;
};

export const filterBlocks = (blocks: Block[]) => {
  // Count instances of identical anchors
  const anchorCount: Record<string, number> = {};

  const anchors: [anchor: string, label: string][] = [];

  const filteredBlocks = blocks.map((block, blockKey) => {
    const heading = maybeGet(block, 'heading');
    const anchorRaw = slugify(heading ?? `block-${blockKey}`);

    anchorCount[anchorRaw] = anchorCount[anchorRaw] ? anchorCount[anchorRaw] + 1 : 1;

    const anchor =
      anchorCount[anchorRaw] > 1 ? `${anchorRaw}-${anchorCount[anchorRaw]}` : anchorRaw;

    if (heading) anchors.push([anchor, heading]);

    return {
      anchorLabel: heading,
      anchor,
      block,
    } as BlockView;
  });

  return {
    anchors,
    blocks: filteredBlocks,
  };
};

// ----------------------------------------------------------------------------------------------------
// --- Type guards ---

export const isBlockType = <T extends BlockTypename>(x: any, typename: T): x is Block<T> => {
  return !!x && x.__typename === typename;
};
